import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Grid, Label, Button, Modal, Header, Segment, Divider, Form, Dropdown, Icon, Checkbox, Loader, Dimmer, Input } from "semantic-ui-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import * as DEEPANALYSIS_ACTIONS from "../../../actions/deepanalysisActions";
import * as TENANT_CLOUD_ACTIONS from "../../../actions/tenantCloudActions";

import _ from "lodash";
import { connect } from "react-redux";
import CrossTableDisplayPage from "./CrossTableDisplayPage";

// Custom styles for the DatePicker component
const datePickerStyles = document.createElement('style');
datePickerStyles.innerHTML = `
    .react-datepicker-popper {
        z-index: 9999 !important;
    }
`;
document.head.appendChild(datePickerStyles);

const CustomDatePickerInput = React.forwardRef(({ value, onClick }, ref) => (
    <Input
        icon="calendar"
        iconPosition="left"
        placeholder="Select Date"
        onClick={onClick}
        value={value}
        ref={ref}
        style={{ borderRadius: '8px', borderColor: '#ccc' }}
    />
));

const CrossTableAnalysisPage = (props) => {
    const { questionnaires = [] } = props;

    // Helper function to get cloud tenants for a specific questionnaire
    const getCloudTenantsForQuestionnaire = (questionnaireId) => {
        const tenantClouds = props.tenantClouds.filter(cloud =>
            cloud.tenants && cloud.tenants.some(tenant => tenant.id === props.tenant.id) &&
            cloud.questionnaires && cloud.questionnaires.some(questionnaire => questionnaire.id === questionnaireId)
        );

        const cloudTenants = [];
        tenantClouds.forEach(cloud => {
            if (cloud.tenants) {
                cloud.tenants.forEach(tenant => {
                    cloudTenants.push(tenant);
                    // if (tenant.id !== props.tenant.id && !cloudTenants.some(ct => ct.id === tenant.id)) {
                    // }
                });
            }
        });

        return cloudTenants;
    };

    const [state, setState] = useState({
        loading: false,
        exportingPdf: false,
        exportingExcel: false,
    });

    // Question selection states
    const [categoricalQuestions, setCategoricalQuestions] = useState([]); // For columns (top)
    const [analysisQuestions, setAnalysisQuestions] = useState([]); // For rows (left)
    const [lockedQuestionnaireId, setLockedQuestionnaireId] = useState(null); // Track questionnaire restriction
    const [isUserSelection, setIsUserSelection] = useState(false); // Track if user manually selected (not pre-loaded)

    // Modal states for categorical questions
    const [showCategoricalQuestionnaireModal, setShowCategoricalQuestionnaireModal] = useState(false);
    const [showCategoricalQuestionsModal, setShowCategoricalQuestionsModal] = useState(false);
    const [selectedCategoricalQuestionnaire, setSelectedCategoricalQuestionnaire] = useState(null);

    // Modal states for analysis questions
    const [showAnalysisQuestionnaireModal, setShowAnalysisQuestionnaireModal] = useState(false);
    const [showAnalysisQuestionsModal, setShowAnalysisQuestionsModal] = useState(false);
    const [selectedAnalysisQuestionnaire, setSelectedAnalysisQuestionnaire] = useState(null);

    const [selectedQnaire, setSelectedQnaire] = useState(null);

    // Cross table data
    const [crossTableData, setCrossTableData] = useState(null);

    // Collapsible segments state
    const [isQuestionSelectionCollapsed, setIsQuestionSelectionCollapsed] = useState(false);
    const [isResultsCollapsed, setIsResultsCollapsed] = useState(false);

    // Individual question section collapse states
    const [isCategoricalSectionCollapsed, setIsCategoricalSectionCollapsed] = useState(false);
    const [isAnalysisSectionCollapsed, setIsAnalysisSectionCollapsed] = useState(false);

    // Hover states for better UX
    const [isQuestionHeaderHovered, setIsQuestionHeaderHovered] = useState(false);
    const [isResultsHeaderHovered, setIsResultsHeaderHovered] = useState(false);
    const [isCategoricalHeaderHovered, setIsCategoricalHeaderHovered] = useState(false);
    const [isAnalysisHeaderHovered, setIsAnalysisHeaderHovered] = useState(false);

    // View toggle state - default to original view (false = original, true = compact)
    const [isCompactView, setIsCompactView] = useState(false);
    const [uniquePerUser, setUniquePerUser] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const hasPermissions = (category) => {
        return (
            !_.isEmpty(props.userPermissions) &&
            props.userPermissions.hasOwnProperty(category)
        );
    };

    const isAdmin = () => {
        return (
            hasPermissions("Other") && props.userPermissions["Other"].actions.admin
        );
    };

    const canReadCrossAnalysis = () => {
        return (hasPermissions('Deep Analysis') && props.userPermissions['Deep Analysis'].actions["VIEW CROSS ANALYSIS"])
            || isAdmin();
    };

    const canCreateCrossAnalysis = () => {
        return hasPermissions('Deep Analysis') && canReadCrossAnalysis() && props.userPermissions['Deep Analysis'].actions["CREATE CROSS ANALYSIS"]
            || isAdmin();
    }

    const questionnaireOptions = questionnaires
        .map(q => ({
            key: q.id,
            value: q,
            text: q.name || q.title || `Questionnaire ${q.id}`,
        }))
        .sort((a, b) => a.text.localeCompare(b.text));

    useEffect(() => {
        if (!_.isEmpty(props.tenant)) {
            if (props.isPermissionsFetched) {
                if (canReadCrossAnalysis()) {
                    props.fetchCrossTableData(props.tenant);
                    props.fetchTenantCloudsAction();
                } else {
                    toast.error('You do not have permission to view this page.');
                }
            }
        }
    }, [props.tenant]);

    // Populate questions from saved crossTableData if available
    useEffect(() => {
        if (props.crossTableData && Array.isArray(props.crossTableData) && props.crossTableData.length > 0) {
            const crossTableDataObj = props.crossTableData[0];

            if (crossTableDataObj) {
                const { categoricalQuestions: savedCategorical, analysisQuestions: savedAnalysis } = crossTableDataObj;

                let preloadedQuestionnaireId = null;

                if (savedCategorical && Array.isArray(savedCategorical) && savedCategorical.length > 0) {
                    setCategoricalQuestions(savedCategorical);
                    if (savedCategorical[0]?.questionnaireId) {
                        preloadedQuestionnaireId = savedCategorical[0].questionnaireId;
                    }
                }

                if (savedAnalysis && Array.isArray(savedAnalysis) && savedAnalysis.length > 0) {
                    setAnalysisQuestions(savedAnalysis);
                    if (!preloadedQuestionnaireId && savedAnalysis[0]?.questionnaireId) {
                        preloadedQuestionnaireId = savedAnalysis[0].questionnaireId;
                    }
                }

                // Set locked questionnaire from pre-loaded data (but don't mark as user selection)
                if (preloadedQuestionnaireId) {
                    setLockedQuestionnaireId(preloadedQuestionnaireId);
                }

                // Show notification if questions were loaded
                if ((savedCategorical && savedCategorical.length > 0) || (savedAnalysis && savedAnalysis.length > 0)) {
                    toast.info("Loaded previously saved questions for cross table analysis");
                }
            }
        }
    }, [props.crossTableData]);

    const validateQuestionnaireSame = () => {
        const allQuestions = [...categoricalQuestions, ...analysisQuestions];
        if (allQuestions.length === 0) return true;

        const questionnaireIds = allQuestions.map(q => q.questionnaireId).filter(Boolean);
        const uniqueIds = [...new Set(questionnaireIds)];

        return uniqueIds.length <= 1;
    };

    const resetAllSelections = () => {
        setCategoricalQuestions([]);
        setAnalysisQuestions([]);
        setLockedQuestionnaireId(null);
        setIsUserSelection(false);
        toast.info("All selections cleared. You can now select from any questionnaire.");
    };

    const generateCrossTable = () => {
        if (categoricalQuestions.length === 0) {
            toast.error("Please select at least 1 categorical question for columns");
            return;
        }

        if (analysisQuestions.length === 0) {
            toast.error("Please select at least 1 analysis question for rows");
            return;
        }

        if (!validateQuestionnaireSame()) {
            toast.error("All questions must be from the same questionnaire. Please reset and select again.");
            return;
        }

        // Update the crossTableData with the new selected questions
        let payload = {
            ...props.crossTableData[0],
            categoricalQuestions: categoricalQuestions,
            analysisQuestions: analysisQuestions,
            uniquePerUser: uniquePerUser,
            startDate: startDate ? startDate.toISOString() : null,
            endDate: endDate ? endDate.toISOString() : null
        };

        props.generateCrossTable(payload, props.tenant);
    };

    const exportCrossTableAsPDF = async () => {
        if (!props.crossTableS3Data?.results) {
            toast.error("No cross table data available to export.");
            return;
        }

        setState(s => ({ ...s, exportingPdf: true }));
        toast.info("Generating PDF... This may take a moment.");

        try {
            const tableElement = document.querySelector('.cross-table-container table');
            if (!tableElement) {
                throw new Error("Cross table not found");
            }

            const canvas = await html2canvas(tableElement, {
                scale: 1.5,
                useCORS: true,
                allowTaint: true,
                scrollY: -window.scrollY,
                scrollX: -window.scrollX,
            });
            const imgData = canvas.toDataURL('image/jpeg', 0.75);

            const pdf = new jsPDF('l', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const imgWidth = pageWidth - 2 * margin;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, Math.min(imgHeight, pageHeight - 2 * margin));
            pdf.save(`cross_table_analysis_${Date.now()}.pdf`);
            toast.success("PDF exported successfully!");
        } catch (error) {
            console.error("Error exporting PDF:", error);
            toast.error("Failed to export PDF. Please try again.");
        } finally {
            setState(s => ({ ...s, exportingPdf: false }));
        }
    };

    const exportCrossTableToExcel = () => {
        if (!props.crossTableS3Data?.results) {
            toast.error("No cross table data available to export.");
            return;
        }

        setState(s => ({ ...s, exportingExcel: true }));

        try {
            const crossTableData = props.crossTableS3Data.results;
            const mainQuestions = Object.keys(crossTableData);
            const subQuestions = Object.keys(crossTableData[mainQuestions[0]]);

            const mainQuestionAnswers = {};
            mainQuestions.forEach(mainQ => {
                const allAnswers = new Set();
                subQuestions.forEach(subQ => {
                    const data = crossTableData[mainQ][subQ]?.data;
                    if (data) {
                        Object.values(data).forEach(categoryData => {
                            Object.keys(categoryData).forEach(answer => {
                                if (answer !== 'Total') allAnswers.add(answer);
                            });
                        });
                    }
                });
                mainQuestionAnswers[mainQ] = Array.from(allAnswers);
            });

            const subQuestionCategories = {};
            subQuestions.forEach(subQ => {
                const data = crossTableData[mainQuestions[0]][subQ]?.data;
                if (data) subQuestionCategories[subQ] = Object.keys(data);
            });

            const excelData = [];
            const headerRow1 = ['', '', ...subQuestions.flatMap(subQ =>
                [...(subQuestionCategories[subQ] || []), 'Total'].map(() => subQ)
            )];
            const headerRow2 = ['Main Question', 'Answer', ...subQuestions.flatMap(subQ =>
                [...(subQuestionCategories[subQ] || []), 'Total']
            )];

            excelData.push(headerRow1);
            excelData.push(headerRow2);

            mainQuestions.forEach(mainQ => {
                mainQuestionAnswers[mainQ].forEach((answer, idx) => {
                    const row = [
                        idx === 0 ? mainQ : '',
                        answer,
                        ...subQuestions.flatMap(subQ => {
                            const categories = subQuestionCategories[subQ] || [];
                            const values = categories.map(category => {
                                const val = crossTableData[mainQ]?.[subQ]?.data?.[category]?.[answer] || 0;
                                return val > 0 ? val : '-';
                            });
                            const total = categories.reduce((sum, category) =>
                                sum + (crossTableData[mainQ]?.[subQ]?.data?.[category]?.[answer] || 0), 0
                            );
                            return [...values, total];
                        })
                    ];
                    excelData.push(row);
                });
            });

            const ws = XLSX.utils.aoa_to_sheet(excelData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Cross Table");
            XLSX.writeFile(wb, `cross_table_analysis_${Date.now()}.xlsx`);

            toast.success("Excel file exported successfully!");
        } catch (error) {
            console.error("Error exporting Excel:", error);
            toast.error("Failed to export Excel file. Please try again.");
        } finally {
            setState(s => ({ ...s, exportingExcel: false }));
        }
    };

    if (!canReadCrossAnalysis()) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <Header as="h2">Access Denied</Header>
                <p>You don't have permission to access this page.</p>
            </div>
        );
    }

    if (props.isLoading) {
        return (
            <div style={{ height: "200px", margin: "10px 0px" }}>
                <Dimmer inverted active>
                    <Loader content='Loading Cross Table Data...' />
                </Dimmer>
            </div>
        );
    }

    return (
        <Form>
            {/* Question Selection Section */}
            <Segment>
                <Grid>
                    <Grid.Row>
                        <Grid.Column width={14}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '5px',
                                borderRadius: '4px',
                                backgroundColor: isQuestionHeaderHovered ? '#f8f9fa' : 'transparent',
                                transition: 'background-color 0.2s'
                            }}
                                onClick={() => setIsQuestionSelectionCollapsed(!isQuestionSelectionCollapsed)}
                                onMouseEnter={() => setIsQuestionHeaderHovered(true)}
                                onMouseLeave={() => setIsQuestionHeaderHovered(false)}>
                                <Icon name={isQuestionSelectionCollapsed ? 'chevron right' : 'chevron down'} />
                                <h3 style={{ margin: 0, marginLeft: '8px' }}>Question Selection</h3>
                                <span style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
                                    {isQuestionSelectionCollapsed ? 'Click to expand' : 'Click to collapse'}
                                </span>
                            </div>
                        </Grid.Column>
                        <Grid.Column floated="right" width={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px' }}>
                            <Button
                                compact
                                size='small'
                                onClick={() => setIsCompactView(!isCompactView)}
                                title={isCompactView ? 'Switch to Table View' : 'Switch to Compact View'}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    minWidth: 'auto',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <Icon name={isCompactView ? 'table' : 'list'} style={{ margin: 0 }} />
                                <span style={{ fontSize: '12px' }}>
                                    {isCompactView ? 'Table' : 'Compact'}
                                </span>
                            </Button>
                            <Form.Button compact primary icon='refresh' size='small' floated='right'
                                onClick={(e) => {
                                    e.preventDefault();
                                    // refreshQnaireData();
                                }}></Form.Button>
                        </Grid.Column>
                    </Grid.Row>

                    {!isQuestionSelectionCollapsed && (
                        <>
                            <Grid.Row>
                                <Grid.Column width={16}>
                                    <p style={{ marginBottom: '15px' }}>Select questions for cross table analysis. Categorical questions will form the columns (top), and analysis questions will form the rows (left).</p>
                                    {lockedQuestionnaireId && isUserSelection && (
                                        <div style={{
                                            padding: '10px',
                                            backgroundColor: '#fff3cd',
                                            border: '1px solid #ffc107',
                                            borderRadius: '4px',
                                            marginBottom: '15px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <div>
                                                <Icon name="lock" color="orange" />
                                                <strong>Questionnaire Locked:</strong> All questions must be from <strong>{questionnaires.find(q => q.id === lockedQuestionnaireId)?.name || 'the same questionnaire'}</strong>
                                            </div>
                                            <Button
                                                size="small"
                                                color="orange"
                                                onClick={resetAllSelections}
                                                icon="undo"
                                                content="Reset All"
                                            />
                                        </div>
                                    )}
                                </Grid.Column>
                            </Grid.Row>

                            {/* Combined Question Selection Row */}
                            {isCompactView ? (
                                /* Compact View */
                                <Grid.Row>
                                    {/* Categorical Questions Column */}
                                    <Grid.Column width={8}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            padding: '6px',
                                            borderRadius: '4px',
                                            backgroundColor: isCategoricalHeaderHovered ? '#e3f2fd' : 'transparent',
                                            transition: 'background-color 0.2s',
                                            marginBottom: '8px'
                                        }}
                                            onClick={() => setIsCategoricalSectionCollapsed(!isCategoricalSectionCollapsed)}
                                            onMouseEnter={() => setIsCategoricalHeaderHovered(true)}
                                            onMouseLeave={() => setIsCategoricalHeaderHovered(false)}>
                                            <Icon name={isCategoricalSectionCollapsed ? 'chevron right' : 'chevron down'} color="blue" />
                                            <Header as="h4" color="blue" style={{ margin: 0, marginLeft: '6px' }}>Categorical Questions (Columns)</Header>
                                        </div>

                                        {!isCategoricalSectionCollapsed && (
                                            <>
                                                <p style={{ fontSize: '13px', marginBottom: '8px' }}>Select categorical questions (yes/no, multiple choice) for columns.</p>
                                                <Button
                                                    fluid
                                                    primary
                                                    icon="plus"
                                                    content="Add Categorical Questions"
                                                    size="small"
                                                    onClick={() => setShowCategoricalQuestionnaireModal(true)}
                                                    disabled={state.loading}
                                                    style={{ marginBottom: '10px' }}
                                                />
                                            </>
                                        )}

                                        {!isCategoricalSectionCollapsed && categoricalQuestions && categoricalQuestions.length > 0 && (
                                            <div style={{ padding: '8px', border: '1px solid #2185d0', borderRadius: '4px', backgroundColor: '#f8fbff' }}>
                                                <Label color="blue" size="small" style={{ marginBottom: '6px' }}>Selected Questions:</Label>
                                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                    {categoricalQuestions.map((cq, index) => (
                                                        <div key={index} style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            padding: '4px 6px',
                                                            marginBottom: '4px',
                                                            backgroundColor: 'white',
                                                            borderRadius: '3px',
                                                            border: '1px solid #e0e0e0'
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                                                <div style={{ backgroundColor: '#2185d0', color: 'white', padding: '1px 4px', borderRadius: '8px', fontSize: '10px', fontWeight: 'bold', marginRight: '6px' }}>
                                                                    {cq.tag || `CQ${index + 1}`}
                                                                </div>
                                                                <span style={{ fontSize: '12px', flex: 1, wordWrap: 'break-word', whiteSpace: 'normal', lineHeight: '1.3' }}>
                                                                    {cq.question || `Question ${cq.id}`}
                                                                </span>
                                                                <div style={{
                                                                    backgroundColor: '#6c757d',
                                                                    color: 'white',
                                                                    padding: '1px 4px',
                                                                    borderRadius: '8px',
                                                                    fontSize: '10px',
                                                                    marginLeft: '4px',
                                                                    marginRight: '4px'
                                                                }}>
                                                                    {cq.questionType}
                                                                </div>
                                                            </div>
                                                            <Button
                                                                icon="trash"
                                                                color="red"
                                                                size="mini"
                                                                compact
                                                                onClick={() => {
                                                                    const updatedCategoricalQuestions = categoricalQuestions.filter((_, i) => i !== index);
                                                                    const retaggedQuestions = updatedCategoricalQuestions.map((q, idx) => ({
                                                                        ...q,
                                                                        tag: `CQ${idx + 1}`
                                                                    }));
                                                                    setCategoricalQuestions(retaggedQuestions);

                                                                    if (retaggedQuestions.length === 0 && analysisQuestions.length === 0) {
                                                                        setLockedQuestionnaireId(null);
                                                                        setIsUserSelection(false);
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div style={{ marginTop: '6px', textAlign: 'right' }}>
                                                    <Button
                                                        size="tiny"
                                                        primary
                                                        icon="plus"
                                                        content="Add More"
                                                        onClick={() => setShowCategoricalQuestionnaireModal(true)}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </Grid.Column>

                                    {/* Analysis Questions Column */}
                                    <Grid.Column width={8}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            padding: '6px',
                                            borderRadius: '4px',
                                            backgroundColor: isAnalysisHeaderHovered ? '#e8f5e8' : 'transparent',
                                            transition: 'background-color 0.2s',
                                            marginBottom: '8px'
                                        }}
                                            onClick={() => setIsAnalysisSectionCollapsed(!isAnalysisSectionCollapsed)}
                                            onMouseEnter={() => setIsAnalysisHeaderHovered(true)}
                                            onMouseLeave={() => setIsAnalysisHeaderHovered(false)}>
                                            <Icon name={isAnalysisSectionCollapsed ? 'chevron right' : 'chevron down'} color="green" />
                                            <Header as="h4" color="green" style={{ margin: 0, marginLeft: '6px' }}>Analysis Questions (Rows)</Header>
                                        </div>

                                        {!isAnalysisSectionCollapsed && (
                                            <>
                                                <p style={{ fontSize: '13px', marginBottom: '8px' }}>Select analysis questions (yes/no, multiple choice) for rows.</p>
                                                <Button
                                                    fluid
                                                    primary
                                                    icon="plus"
                                                    content="Add Analysis Questions"
                                                    size="small"
                                                    onClick={() => setShowAnalysisQuestionnaireModal(true)}
                                                    disabled={state.loading}
                                                    style={{ marginBottom: '10px' }}
                                                />
                                            </>
                                        )}

                                        {!isAnalysisSectionCollapsed && analysisQuestions && analysisQuestions.length > 0 && (
                                            <div style={{ padding: '8px', border: '1px solid #21ba45', borderRadius: '4px', backgroundColor: '#f8fff8' }}>
                                                <Label color="green" size="small" style={{ marginBottom: '6px' }}>Selected Questions:</Label>
                                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                    {analysisQuestions.map((aq, index) => (
                                                        <div key={index} style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            padding: '4px 6px',
                                                            marginBottom: '4px',
                                                            backgroundColor: 'white',
                                                            borderRadius: '3px',
                                                            border: '1px solid #e0e0e0'
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                                                <div style={{ backgroundColor: '#21ba45', color: 'white', padding: '1px 4px', borderRadius: '8px', fontSize: '10px', fontWeight: 'bold', marginRight: '6px' }}>
                                                                    {aq.tag || `AQ${index + 1}`}
                                                                </div>
                                                                <span style={{ fontSize: '12px', flex: 1, wordWrap: 'break-word', whiteSpace: 'normal', lineHeight: '1.3' }}>
                                                                    {aq.question || `Question ${aq.id}`}
                                                                </span>
                                                                <div style={{
                                                                    backgroundColor: '#6c757d',
                                                                    color: 'white',
                                                                    padding: '1px 4px',
                                                                    borderRadius: '8px',
                                                                    fontSize: '10px',
                                                                    marginLeft: '4px',
                                                                    marginRight: '4px'
                                                                }}>
                                                                    {aq.questionType}
                                                                </div>
                                                            </div>
                                                            <Button
                                                                icon="trash"
                                                                color="red"
                                                                size="mini"
                                                                compact
                                                                onClick={() => {
                                                                    const updatedAnalysisQuestions = analysisQuestions.filter((_, i) => i !== index);
                                                                    const retaggedQuestions = updatedAnalysisQuestions.map((q, idx) => ({
                                                                        ...q,
                                                                        tag: `AQ${idx + 1}`
                                                                    }));
                                                                    setAnalysisQuestions(retaggedQuestions);

                                                                    if (retaggedQuestions.length === 0 && categoricalQuestions.length === 0) {
                                                                        setLockedQuestionnaireId(null);
                                                                        setIsUserSelection(false);
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div style={{ marginTop: '6px', textAlign: 'right' }}>
                                                    <Button
                                                        size="tiny"
                                                        primary
                                                        icon="plus"
                                                        content="Add More"
                                                        onClick={() => setShowAnalysisQuestionnaireModal(true)}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </Grid.Column>
                                </Grid.Row>
                            ) : (
                                /* Original Table View */
                                <>
                                    {/* Categorical Questions Section */}
                                    <Grid.Row>
                                        <Grid.Column width={16}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                backgroundColor: isCategoricalHeaderHovered ? '#e3f2fd' : 'transparent',
                                                transition: 'background-color 0.2s',
                                                marginBottom: '10px'
                                            }}
                                                onClick={() => setIsCategoricalSectionCollapsed(!isCategoricalSectionCollapsed)}
                                                onMouseEnter={() => setIsCategoricalHeaderHovered(true)}
                                                onMouseLeave={() => setIsCategoricalHeaderHovered(false)}>
                                                <Icon name={isCategoricalSectionCollapsed ? 'chevron right' : 'chevron down'} color="blue" />
                                                <Header as="h4" color="blue" style={{ margin: 0, marginLeft: '8px' }}>Categorical Questions (Columns)</Header>
                                                <span style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
                                                    {isCategoricalSectionCollapsed ? 'Click to expand' : 'Click to collapse'}
                                                </span>
                                            </div>

                                            {!isCategoricalSectionCollapsed && (
                                                <>
                                                    <p>Select categorical questions (yes/no, multiple choice) that will appear across the top of the cross table.</p>
                                                    <Button
                                                        fluid
                                                        primary
                                                        icon="plus"
                                                        content="Add Categorical Questions"
                                                        onClick={() => setShowCategoricalQuestionnaireModal(true)}
                                                        disabled={state.loading}
                                                    />
                                                </>
                                            )}
                                        </Grid.Column>
                                    </Grid.Row>

                                    {!isCategoricalSectionCollapsed && categoricalQuestions && categoricalQuestions.length > 0 && (
                                        <Grid.Row>
                                            <Grid.Column width={16}>
                                                <div style={{ padding: '12px', border: '1px solid #2185d0', borderRadius: '4px', backgroundColor: '#f8fbff' }}>
                                                    <Label color="blue" style={{ marginBottom: '8px' }}>Selected Categorical Questions (Columns):</Label>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                                                        <thead>
                                                            <tr style={{ borderBottom: '1px solid #ddd' }}>
                                                                <th style={{ padding: '8px', textAlign: 'left', width: '80px' }}>Tag</th>
                                                                <th style={{ padding: '8px', textAlign: 'left' }}>Question</th>
                                                                <th style={{ padding: '8px', textAlign: 'left' }}>Type</th>
                                                                <th style={{ padding: '8px', textAlign: 'center', width: '120px' }}>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {categoricalQuestions.map((cq, index) => (
                                                                <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                                                                    <td style={{ padding: '8px' }}>
                                                                        <div style={{ backgroundColor: '#2185d0', color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '12px', display: 'inline-block', fontWeight: 'bold' }}>
                                                                            {cq.tag || `CQ${index + 1}`}
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ padding: '8px' }}>
                                                                        <span>{cq.question || `Question ${cq.id}`}</span>
                                                                    </td>
                                                                    <td style={{ padding: '8px' }}>
                                                                        <div style={{
                                                                            backgroundColor: '#6c757d',
                                                                            color: 'white',
                                                                            padding: '2px 6px',
                                                                            borderRadius: '10px',
                                                                            fontSize: '12px',
                                                                            display: 'inline-block'
                                                                        }}>
                                                                            {cq.questionType}
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                                                        <Button
                                                                            icon="trash"
                                                                            color="red"
                                                                            size="mini"
                                                                            onClick={() => {
                                                                                const updatedCategoricalQuestions = categoricalQuestions.filter((_, i) => i !== index);
                                                                                const retaggedQuestions = updatedCategoricalQuestions.map((q, idx) => ({
                                                                                    ...q,
                                                                                    tag: `CQ${idx + 1}`
                                                                                }));
                                                                                setCategoricalQuestions(retaggedQuestions);

                                                                                if (retaggedQuestions.length === 0 && analysisQuestions.length === 0) {
                                                                                    setLockedQuestionnaireId(null);
                                                                                    setIsUserSelection(false);
                                                                                }
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    <div style={{ marginTop: '10px', textAlign: 'right' }}>
                                                        <Button
                                                            size="small"
                                                            primary
                                                            icon="plus"
                                                            content="Add More Categorical Questions"
                                                            onClick={() => setShowCategoricalQuestionnaireModal(true)}
                                                        />
                                                    </div>
                                                </div>
                                            </Grid.Column>
                                        </Grid.Row>
                                    )}

                                    <Divider />

                                    {/* Analysis Questions Section */}
                                    <Grid.Row>
                                        <Grid.Column width={16}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                backgroundColor: isAnalysisHeaderHovered ? '#e8f5e8' : 'transparent',
                                                transition: 'background-color 0.2s',
                                                marginBottom: '10px'
                                            }}
                                                onClick={() => setIsAnalysisSectionCollapsed(!isAnalysisSectionCollapsed)}
                                                onMouseEnter={() => setIsAnalysisHeaderHovered(true)}
                                                onMouseLeave={() => setIsAnalysisHeaderHovered(false)}>
                                                <Icon name={isAnalysisSectionCollapsed ? 'chevron right' : 'chevron down'} color="green" />
                                                <Header as="h4" color="green" style={{ margin: 0, marginLeft: '8px' }}>Analysis Questions (Rows)</Header>
                                                <span style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
                                                    {isAnalysisSectionCollapsed ? 'Click to expand' : 'Click to collapse'}
                                                </span>
                                            </div>

                                            {!isAnalysisSectionCollapsed && (
                                                <>
                                                    <p>Select analysis questions that will appear down the left side of the cross table.</p>
                                                    <Button
                                                        fluid
                                                        primary
                                                        icon="plus"
                                                        content="Add Analysis Questions"
                                                        onClick={() => setShowAnalysisQuestionnaireModal(true)}
                                                        disabled={state.loading}
                                                    />
                                                </>
                                            )}
                                        </Grid.Column>
                                    </Grid.Row>

                                    {!isAnalysisSectionCollapsed && analysisQuestions && analysisQuestions.length > 0 && (
                                        <Grid.Row>
                                            <Grid.Column width={16}>
                                                <div style={{ padding: '12px', border: '1px solid #21ba45', borderRadius: '4px', backgroundColor: '#f8fff8' }}>
                                                    <Label color="green" style={{ marginBottom: '8px' }}>Selected Analysis Questions (Rows):</Label>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                                                        <thead>
                                                            <tr style={{ borderBottom: '1px solid #ddd' }}>
                                                                <th style={{ padding: '8px', textAlign: 'left', width: '80px' }}>Tag</th>
                                                                <th style={{ padding: '8px', textAlign: 'left' }}>Question</th>
                                                                <th style={{ padding: '8px', textAlign: 'left' }}>Type</th>
                                                                <th style={{ padding: '8px', textAlign: 'center', width: '120px' }}>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {analysisQuestions.map((aq, index) => (
                                                                <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                                                                    <td style={{ padding: '8px' }}>
                                                                        <div style={{ backgroundColor: '#21ba45', color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '12px', display: 'inline-block', fontWeight: 'bold' }}>
                                                                            {aq.tag || `AQ${index + 1}`}
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ padding: '8px' }}>
                                                                        <span>{aq.question || `Question ${aq.id}`}</span>
                                                                    </td>
                                                                    <td style={{ padding: '8px' }}>
                                                                        <div style={{
                                                                            backgroundColor: '#6c757d',
                                                                            color: 'white',
                                                                            padding: '2px 6px',
                                                                            borderRadius: '10px',
                                                                            fontSize: '12px',
                                                                            display: 'inline-block'
                                                                        }}>
                                                                            {aq.questionType}
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                                                        <Button
                                                                            icon="trash"
                                                                            color="red"
                                                                            size="mini"
                                                                            onClick={() => {
                                                                                const updatedAnalysisQuestions = analysisQuestions.filter((_, i) => i !== index);
                                                                                const retaggedQuestions = updatedAnalysisQuestions.map((q, idx) => ({
                                                                                    ...q,
                                                                                    tag: `AQ${idx + 1}`
                                                                                }));
                                                                                setAnalysisQuestions(retaggedQuestions);

                                                                                if (retaggedQuestions.length === 0 && categoricalQuestions.length === 0) {
                                                                                    setLockedQuestionnaireId(null);
                                                                                    setIsUserSelection(false);
                                                                                }
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    <div style={{ marginTop: '10px', textAlign: 'right' }}>
                                                        <Button
                                                            size="small"
                                                            primary
                                                            icon="plus"
                                                            content="Add More Analysis Questions"
                                                            onClick={() => setShowAnalysisQuestionnaireModal(true)}
                                                        />
                                                    </div>
                                                </div>
                                            </Grid.Column>
                                        </Grid.Row>
                                    )}
                                </>
                            )}

                            <Grid.Row>
                                <Grid.Column width={5}>
                                    <Grid.Column>
                                        <Label style={{ marginBottom: '8px' }}>Start Date:</Label>
                                    </Grid.Column>
                                    <Grid.Column>
                                        <DatePicker
                                            selected={startDate}
                                            onChange={(date) => setStartDate(date)}
                                            endDate={endDate}
                                            maxDate={endDate}
                                            dateFormat="dd-MM-yyyy"
                                            isClearable
                                            customInput={<CustomDatePickerInput />}
                                            popperClassName="datepicker-popper"
                                            popperPlacement="bottom-start"
                                            popperModifiers={{
                                                preventOverflow: {
                                                    enabled: true,
                                                    escapeWithReference: false,
                                                    boundariesElement: 'viewport'
                                                }
                                            }}
                                        />
                                    </Grid.Column>
                                </Grid.Column>
                                <Grid.Column width={5}>
                                    <Grid.Column>
                                        <Label style={{ marginBottom: '8px' }}>End Date:</Label>
                                    </Grid.Column>
                                    <Grid.Column>
                                        <DatePicker
                                            selected={endDate}
                                            onChange={(date) => setEndDate(date)}
                                            startDate={startDate}
                                            minDate={startDate}
                                            dateFormat="dd-MM-yyyy"
                                            isClearable
                                            customInput={<CustomDatePickerInput />}
                                            popperClassName="datepicker-popper"
                                            popperPlacement="bottom-start"
                                            popperModifiers={{
                                                preventOverflow: {
                                                    enabled: true,
                                                    escapeWithReference: false,
                                                    boundariesElement: 'viewport'
                                                }
                                            }}
                                        />
                                    </Grid.Column>
                                </Grid.Column>
                                <Grid.Column width={6}>
                                    <div style={{ marginTop: '32px' }}>
                                        <Checkbox
                                            toggle
                                            label="Unique per user"
                                            checked={uniquePerUser}
                                            onChange={(e, { checked }) => setUniquePerUser(checked)}
                                        />
                                    </div>
                                </Grid.Column>
                            </Grid.Row>

                            <Grid.Row>
                                <Grid.Column width={16}>
                                    <Button
                                        color="teal"
                                        size="large"
                                        onClick={generateCrossTable}
                                        disabled={categoricalQuestions.length === 0 || analysisQuestions.length === 0 || props.isLoading}
                                        loading={props.isLoading}
                                        icon="table"
                                        content="Generate Cross Table"
                                    />
                                    {/* <Button
                                        color="green"
                                        size="large"
                                        onClick={exportCrossTableAsPDF}
                                        disabled={!props.crossTableS3Data?.results || state.exportingPdf}
                                        loading={state.exportingPdf}
                                        icon="file pdf"
                                        content="Export PDF"
                                        style={{ marginLeft: '10px' }}
                                    /> */}
                                    <Button
                                        color="olive"
                                        size="large"
                                        onClick={exportCrossTableToExcel}
                                        disabled={!props.crossTableS3Data?.results || state.exportingExcel}
                                        loading={state.exportingExcel}
                                        icon="file excel"
                                        content="Export Excel"
                                        style={{ marginLeft: '10px' }}
                                    />
                                    {(categoricalQuestions.length === 0 || analysisQuestions.length === 0) && (
                                        <div style={{ marginTop: '10px', color: '#f56565', fontSize: '14px' }}>
                                            Please select at least one categorical question and one analysis question to generate the cross table.
                                        </div>
                                    )}
                                </Grid.Column>
                            </Grid.Row>
                        </>
                    )}
                </Grid>
            </Segment>

            {/* Cross Table Display Section */}
            <Segment>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: '15px',
                    padding: '5px',
                    borderRadius: '4px',
                    backgroundColor: isResultsHeaderHovered ? '#f8f9fa' : 'transparent',
                    transition: 'background-color 0.2s'
                }}
                    onClick={() => setIsResultsCollapsed(!isResultsCollapsed)}
                    onMouseEnter={() => setIsResultsHeaderHovered(true)}
                    onMouseLeave={() => setIsResultsHeaderHovered(false)}>
                    <Icon name={isResultsCollapsed ? 'chevron right' : 'chevron down'} />
                    <Header as="h3" style={{ margin: 0, marginLeft: '8px' }}>Cross Table Results</Header>
                    <span style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
                        {isResultsCollapsed ? 'Click to expand' : 'Click to collapse'}
                    </span>
                    {!isResultsCollapsed && props.crossTableData && props.crossTableData.length > 0 && (
                        <div style={{ marginLeft: 'auto' }}>
                            <Button
                                size="small"
                                primary
                                icon="refresh"
                                content="Refresh Results"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.clearCrossTableS3Data();
                                    props.fetchCrossTableS3Data(props.crossTableData, props.tenant);
                                }}
                                loading={props.crossTableS3Data?.loading}
                                disabled={props.crossTableS3Data?.loading}
                            />
                        </div>
                    )}
                </div>

                {!isResultsCollapsed && (
                    <>
                        {props.crossTableS3Data?.loading && (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                <Icon name="spinner" loading />
                                <p>Loading cross table analysis results...</p>
                            </div>
                        )}

                        {props.crossTableS3Data?.error && (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#d32f2f', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                                <Icon name="exclamation triangle" />
                                <p>{props.crossTableS3Data.error.message}</p>
                                <Button
                                    size="small"
                                    primary
                                    onClick={() => {
                                        if (props.crossTableData && props.crossTableData.length > 0) {
                                            props.fetchCrossTableS3Data(props.crossTableData, props.tenant);
                                        }
                                    }}
                                >
                                    Retry
                                </Button>
                            </div>
                        )}

                        {props.crossTableS3Data?.results ? (
                            <CrossTableDisplayPage crossTableData={props.crossTableS3Data.results} />
                        ) : !props.crossTableS3Data?.loading && !props.crossTableS3Data?.error && (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                <p>No cross table generated yet. Select questions and click "Generate Cross Table" to see results.</p>
                            </div>
                        )}
                    </>
                )}
            </Segment>

            {/* Categorical Questions Questionnaire Selection Modal */}
            <Modal open={showCategoricalQuestionnaireModal} onClose={() => {
                setShowCategoricalQuestionnaireModal(false);
                setSelectedCategoricalQuestionnaire(null);
            }} size="small">
                <Header>Select Questionnaire for Categorical Questions</Header>
                <Modal.Content>
                    <div style={{ marginBottom: '15px' }}>
                        <p>Choose a questionnaire to select categorical questions from:</p>
                    </div>
                    {questionnaireOptions && questionnaireOptions.length > 0 ? (
                        <Dropdown
                            placeholder="Select Questionnaire"
                            fluid
                            search
                            selection
                            options={lockedQuestionnaireId
                                ? questionnaireOptions.filter(opt => opt.value.id === lockedQuestionnaireId)
                                : questionnaireOptions
                            }
                            value={selectedAnalysisQuestionnaire}
                            onChange={(e, { value }) => {
                                if (lockedQuestionnaireId && value.id !== lockedQuestionnaireId) {
                                    toast.warning(`You can only select questions from ${questionnaires.find(q => q.id === lockedQuestionnaireId)?.name}. Reset selections to choose a different questionnaire.`);
                                    return;
                                }
                                setSelectedCategoricalQuestionnaire(value);
                                setSelectedQnaire(value);
                                props.fetchQuestionnaireQuestions(value.id, null, value.tenantId);
                                setShowCategoricalQuestionnaireModal(false);
                                setTimeout(() => setShowCategoricalQuestionsModal(true), 500);
                            }}
                        />
                    ) : (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <p>No questionnaires available.</p>
                        </div>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button color="red" onClick={() => {
                        setShowCategoricalQuestionnaireModal(false);
                        setSelectedCategoricalQuestionnaire(null);
                    }}>Cancel</Button>
                </Modal.Actions>
            </Modal>

            {/* Categorical Questions Selection Modal */}
            <Modal open={showCategoricalQuestionsModal} onClose={() => {
                setShowCategoricalQuestionsModal(false);
                setSelectedCategoricalQuestionnaire(null);
            }} size="large">
                <Header>Select Categorical Questions for Columns</Header>
                <Modal.Content>
                    <div style={{ marginBottom: '15px' }}>
                        <p><strong>Questionnaire:</strong> {selectedCategoricalQuestionnaire?.name || selectedCategoricalQuestionnaire?.title || selectedQnaire?.name || selectedQnaire?.title || 'Selected Questionnaire'}</p>
                        <p><strong>Instructions:</strong></p>
                        <ul>
                            <li>Select <strong>categorical questions</strong> (yes_no, mc_sel_one, multi_select) that will appear as columns</li>
                            <li>These questions will form the top headers of your cross table</li>
                            <li>You can select multiple categorical questions</li>
                        </ul>
                    </div>
                    {props.graphQuestions && props.graphQuestions.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Question</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Type</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', width: '100px' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {props.graphQuestions
                                    .filter(question => ['yes_no', 'mc_sel_one', 'multi_select'].includes(question.questionType))
                                    .map((question, index) => {
                                        const isAlreadySelected = categoricalQuestions.some(cq => cq.id === question.id);

                                        return (
                                            <tr key={question.id} style={{
                                                borderBottom: '1px solid #eee',
                                                backgroundColor: isAlreadySelected ? '#f0f8ff' : 'transparent'
                                            }}>
                                                <td style={{ padding: '12px' }}>{question.question}</td>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{
                                                        backgroundColor: '#2185d0',
                                                        color: 'white',
                                                        padding: '2px 6px',
                                                        borderRadius: '10px',
                                                        fontSize: '12px',
                                                        display: 'inline-block'
                                                    }}>
                                                        {question.questionType}
                                                    </div>
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <Button
                                                        size="mini"
                                                        color="blue"
                                                        disabled={isAlreadySelected}
                                                        onClick={() => {
                                                            const questionnaireId = selectedCategoricalQuestionnaire?.id || selectedQnaire?.id;

                                                            if (!lockedQuestionnaireId) {
                                                                setLockedQuestionnaireId(questionnaireId);
                                                                setIsUserSelection(true);
                                                            }

                                                            const existingIndex = categoricalQuestions.findIndex(q => q.id === question.id);

                                                            if (existingIndex >= 0) {
                                                                const updatedList = [...categoricalQuestions];
                                                                updatedList[existingIndex] = {
                                                                    ...updatedList[existingIndex],
                                                                    id: question.id,
                                                                    question: question.question,
                                                                    questionType: question.questionType,
                                                                    lookupId: question.lookupId,
                                                                    questionnaireId: questionnaireId
                                                                };
                                                                setCategoricalQuestions(updatedList);
                                                            } else {
                                                                const nextTagNumber = categoricalQuestions.length + 1;
                                                                const categoricalQuestionObj = {
                                                                    id: question.id,
                                                                    question: question.question,
                                                                    questionType: question.questionType,
                                                                    lookupId: question.lookupId,
                                                                    questionnaireId: questionnaireId,
                                                                    tag: `CQ${nextTagNumber}`
                                                                };
                                                                setCategoricalQuestions([...categoricalQuestions, categoricalQuestionObj]);
                                                            }
                                                        }}
                                                    >
                                                        {isAlreadySelected ? 'Selected' : 'Select'}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <p>No categorical questions available. Please select a questionnaire first.</p>
                        </div>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button color="red" onClick={() => {
                        setShowCategoricalQuestionsModal(false);
                        setSelectedCategoricalQuestionnaire(null);
                    }}>Close</Button>
                </Modal.Actions>
            </Modal>

            {/* Analysis Questions Questionnaire Selection Modal */}
            <Modal open={showAnalysisQuestionnaireModal} onClose={() => {
                setShowAnalysisQuestionnaireModal(false);
                setSelectedAnalysisQuestionnaire(null);
            }} size="small">
                <Header>Select Questionnaire for Analysis</Header>
                <Modal.Content>
                    <div style={{ marginBottom: '15px' }}>
                        <p>Choose a questionnaire to select questions from:</p>
                    </div>
                    {questionnaireOptions && questionnaireOptions.length > 0 ? (
                        <Dropdown
                            placeholder="Select Questionnaire"
                            fluid
                            search
                            selection
                            options={lockedQuestionnaireId
                                ? questionnaireOptions.filter(opt => opt.value.id === lockedQuestionnaireId)
                                : questionnaireOptions
                            }
                            value={selectedAnalysisQuestionnaire}
                            onChange={(e, { value }) => {
                                if (lockedQuestionnaireId && value.id !== lockedQuestionnaireId) {
                                    toast.warning(`You can only select questions from ${questionnaires.find(q => q.id === lockedQuestionnaireId)?.name}. Reset selections to choose a different questionnaire.`);
                                    return;
                                }
                                setSelectedAnalysisQuestionnaire(value);
                                setSelectedQnaire(value);
                                props.fetchQuestionnaireQuestions(value.id, null, value.tenantId);
                                setShowAnalysisQuestionnaireModal(false);
                                setTimeout(() => setShowAnalysisQuestionsModal(true), 500);
                            }}
                        />
                    ) : (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <p>No questionnaires available.</p>
                        </div>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button color="red" onClick={() => {
                        setShowAnalysisQuestionnaireModal(false);
                        setSelectedAnalysisQuestionnaire(null);
                    }}>Cancel</Button>
                </Modal.Actions>
            </Modal>

            {/* Analysis Questions Selection Modal */}
            <Modal open={showAnalysisQuestionsModal} onClose={() => {
                setShowAnalysisQuestionsModal(false);
                setSelectedAnalysisQuestionnaire(null);
            }} size="large">
                <Header>Select Analysis Questions for Rows</Header>
                <Modal.Content>
                    <div style={{ marginBottom: '15px' }}>
                        <p><strong>Questionnaire:</strong> {selectedAnalysisQuestionnaire?.name || selectedAnalysisQuestionnaire?.title || selectedQnaire?.name || selectedQnaire?.title || 'Selected Questionnaire'}</p>
                        <p><strong>Instructions:</strong></p>
                        <ul>
                            <li>Select <strong>analysis questions</strong> of any type that will appear as rows</li>
                            <li>These questions will form the left side labels of your cross table</li>
                            <li>You can select multiple analysis questions</li>
                            <li>All question types are supported for analysis questions</li>
                        </ul>
                    </div>
                    {props.graphQuestions && props.graphQuestions.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f8f9fa' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Question</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Type</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', width: '100px' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {props.graphQuestions
                                    .map((question, index) => {
                                        const isAlreadySelected = analysisQuestions.some(aq => aq.id === question.id);

                                        return (
                                            <tr key={question.id} style={{
                                                borderBottom: '1px solid #eee',
                                                backgroundColor: isAlreadySelected ? '#f0fff0' : 'transparent'
                                            }}>
                                                <td style={{ padding: '12px' }}>{question.question}</td>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{
                                                        backgroundColor: '#21ba45',
                                                        color: 'white',
                                                        padding: '2px 6px',
                                                        borderRadius: '10px',
                                                        fontSize: '12px',
                                                        display: 'inline-block'
                                                    }}>
                                                        {question.questionType}
                                                    </div>
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <Button
                                                        size="mini"
                                                        color="green"
                                                        disabled={isAlreadySelected}
                                                        onClick={() => {
                                                            const questionnaireId = selectedAnalysisQuestionnaire?.id || selectedQnaire?.id;

                                                            if (!lockedQuestionnaireId) {
                                                                setLockedQuestionnaireId(questionnaireId);
                                                                setIsUserSelection(true);
                                                            }

                                                            const existingIndex = analysisQuestions.findIndex(q => q.id === question.id);

                                                            if (existingIndex >= 0) {
                                                                const updatedList = [...analysisQuestions];
                                                                updatedList[existingIndex] = {
                                                                    ...updatedList[existingIndex],
                                                                    id: question.id,
                                                                    question: question.question,
                                                                    questionType: question.questionType,
                                                                    lookupId: question.lookupId,
                                                                    questionnaireId: questionnaireId
                                                                };
                                                                setAnalysisQuestions(updatedList);
                                                            } else {
                                                                const nextTagNumber = analysisQuestions.length + 1;
                                                                const analysisQuestionObj = {
                                                                    id: question.id,
                                                                    question: question.question,
                                                                    questionType: question.questionType,
                                                                    lookupId: question.lookupId,
                                                                    questionnaireId: questionnaireId,
                                                                    tag: `AQ${nextTagNumber}`,
                                                                    cloudTenants: getCloudTenantsForQuestionnaire(selectedAnalysisQuestionnaire?.id || selectedQnaire?.id),
                                                                };
                                                                setAnalysisQuestions([...analysisQuestions, analysisQuestionObj]);
                                                            }
                                                        }}
                                                    >
                                                        {isAlreadySelected ? 'Selected' : 'Select'}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <p>No questions available. Please select a questionnaire first.</p>
                        </div>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button color="red" onClick={() => {
                        setShowAnalysisQuestionsModal(false);
                        setSelectedAnalysisQuestionnaire(null);
                    }}>Close</Button>
                </Modal.Actions>
            </Modal>
        </Form>
    );
};

const questionTypes = ['yes_no', 'mc_sel_one', 'multi_select', 'lickert'];

const mapStateToProps = (state) => {
    return {
        tenant: state.tenantManagement.activeTenant,
        userPermissions: !_.isEmpty(state.authUserRoles)
            ? state.authUserRoles.permissions
            : state.authUserRoles.permissions,
        isPermissionsFetched: state.isPermissionsFetched === true,
        graphQuestions: Array.isArray(state.deepAnalysisQuestions)
            ? state.deepAnalysisQuestions.filter(q => questionTypes.includes(q.questionType))
            : [],
        crossTableData: state.crossTableData,
        crossTableS3Data: state.crossTableS3Data,
        tenantClouds: state.tenantClouds || [],
        isLoading: state.fetchingData === true,
    };
};

export default connect(
    mapStateToProps,
    (dispatch) => ({
        fetchQuestionnaireQuestions: (questionaireId, sectionId, tenantId) => dispatch(DEEPANALYSIS_ACTIONS.fetchQuestionsAction(questionaireId, sectionId, tenantId)),
        fetchTenantCloudsAction: () => dispatch(TENANT_CLOUD_ACTIONS.fetchTenantCloudsAction()),
        fetchCrossTableData: (tenant) => dispatch(DEEPANALYSIS_ACTIONS.fetchCrossTableDataAction(tenant)),
        fetchCrossTableS3Data: (crossTableData, tenant) => dispatch(DEEPANALYSIS_ACTIONS.fetchCrossTableS3DataAction(crossTableData, tenant)),
        clearCrossTableS3Data: () => dispatch(DEEPANALYSIS_ACTIONS.clearCrossTableS3DataAction()),
        generateCrossTable: (payload, tenant) => dispatch(DEEPANALYSIS_ACTIONS.generateCrossTable(payload, tenant)),
    })
)(CrossTableAnalysisPage);